import { AccountCreateModal } from '@account'
import { CurrencyIcon, DropdownOptionRow, Input, MMButton } from '@components'
import cn from '@integrations/classnames'
import type { ReactNode } from '@integrations/react'
import { isValidElement, useContext, useEffect, useMemo, useState } from '@integrations/react'
import { isOwnerOrAdministrator, selectCurrentUserRole, useAppSelector } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, TradeableAsset } from 'mmfintech-backend-api/types'
import { formatMoney, GlobalContext, isValidString, tr } from 'mmfintech-commons'
import { InputLabel } from 'mmfintech-portal-commons'

import surfaceStyles from '../SelectorSurface/SelectorSurface.module.scss'
import styles from './SelectAccount.module.scss'

interface Props {
  bordered?: boolean
  staticDisplay?: boolean // if set, static account display will be presented
  accounts?: AccountBalanceResponse[] // list of accounts to select from
  error?: string // error message (if needed)
  filteredCurrencies?: TradeableAsset['currency'][]
  label?: string // label above the selection box
  showAddButton?: boolean // if set an "Add New Account" button will be added at the end of the list
  showAllAccounts?: boolean // if set, "All Account" option will be added at the beginning of the list
  selectedAccount?: AccountBalanceResponse // selected account
  setSelectedAccount?: (account: AccountBalanceResponse) => void // selected account setter
  required?: boolean
  loading?: boolean
  dataTest?: string
  extraSection?: ReactNode
  hasExchangeAssets?: boolean
  compact?: boolean // compact pill-style control; the selected value shows the name only
}

interface AccountOptionData {
  value: string
  label: ReactNode
  selectedLabel: ReactNode
  hidden?: boolean // only there so the control can show the selected account; kept out of the menu
}

const ALL_ACCOUNTS_VALUE = ''

function accountName({ id, name }: AccountBalanceResponse) {
  return name || `${tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.ACCOUNT_NUM_LABEL', 'Account №')} ${id}`
}

function accountBalance({ balance, currencyCode }: AccountBalanceResponse) {
  return `${tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.BALANCE_LABEL', 'Balance')}: ${formatMoney(balance, currencyCode)}`
}

function SelectAccount({
  bordered = false,
  staticDisplay = false,
  accounts,
  error,
  label,
  showAddButton = false,
  showAllAccounts = false,
  selectedAccount,
  setSelectedAccount,
  required,
  loading,
  dataTest,
  extraSection = null,
  hasExchangeAssets,
  filteredCurrencies,
  compact = false
}: Props) {
  const customerRole = useAppSelector(selectCurrentUserRole)
  const { modalShow } = useContext(GlobalContext)
  const [preselectAccountId, setPreselectAccountId] = useState(0)

  const list = Array.isArray(accounts) ? accounts : []

  function handleChange(_name: string, value: string) {
    setSelectedAccount?.(value === ALL_ACCOUNTS_VALUE ? null : list.find((account) => String(account.id) === value))
  }

  function handleCreateSuccess(_currency: string, accountId: number) {
    setPreselectAccountId(Number(accountId))
  }

  // react-select keeps its menu open when something inside it is clicked; blurring the select closes it.
  function closeMenu() {
    ;(document.activeElement as HTMLElement | null)?.blur()
  }

  function handleNewAccountClick() {
    closeMenu()
    modalShow({
      options: { closeOnClickOutside: false, size: 'auto', overflow: 'none' },
      content: <AccountCreateModal filteredCurrencies={filteredCurrencies} onSuccess={handleCreateSuccess} />
    })
  }

  useEffect(() => {
    if (preselectAccountId) {
      const find = list.find((account) => account.id === preselectAccountId)
      setPreselectAccountId(0)
      setSelectedAccount?.(find)
    }
    // eslint-disable-next-line
  }, [accounts, preselectAccountId])

  const allAccountsCaption = tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.ALL_ACCOUNTS', 'All accounts')

  const options: AccountOptionData[] = useMemo(() => {
    function accountOption(account: AccountBalanceResponse, hidden = false): AccountOptionData {
      const row = (
        <DropdownOptionRow
          key={account.id}
          icon={<CurrencyIcon currency={account.currencyCode} size='26px' />}
          title={accountName(account)}
          description={account.id ? accountBalance(account) : undefined} // "Other currencies" entries have no account yet
        />
      )

      return { value: String(account.id), label: row, selectedLabel: compact ? accountName(account) : row, hidden }
    }

    // The select can only show an account that is one of its options. Static previews pass `selectedAccount` without
    // `accounts`, and "Other currencies" picks an account that is not in the list, so add it as a hidden option.
    const missingSelected = selectedAccount && !list.some((account) => account.id === selectedAccount.id)

    return [
      ...(showAllAccounts
        ? [
            {
              value: ALL_ACCOUNTS_VALUE,
              label: <DropdownOptionRow reserveIcon title={allAccountsCaption} />,
              selectedLabel: allAccountsCaption
            }
          ]
        : []),
      ...list.map((account) => accountOption(account)),
      ...(missingSelected ? [accountOption(selectedAccount, true)] : [])
    ]
  }, [accounts, selectedAccount, showAllAccounts, compact, allAccountsCaption])

  const addButton =
    showAddButton && isOwnerOrAdministrator(customerRole) ? (
      <div className={cn(styles['add-button-wrapper'], 'add-button-wrapper')}>
        <MMButton
          type='button'
          variant='round-secondary'
          caption={tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.ADD_BUTTON', 'Add new account')}
          onClick={handleNewAccountClick}
        />
      </div>
    ) : null

  const menuFooter = (
    <>
      {addButton}
      {isValidElement(extraSection) ? <div onClick={closeMenu}>{extraSection}</div> : null}
    </>
  )

  const noAccountsMessage = hasExchangeAssets
    ? tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.CANNOT_EXCHANGE', 'Currently this asset cannot be exchanged')
    : tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.NO_ACCOUNTS', 'No Eligible Accounts')

  return (
    <div className={cn(surfaceStyles['selector-wrapper'], 'select-account-wrapper')}>
      <InputLabel label={label} required={required} requiredPosition='right' />
      <div
        className={cn(styles['select-account'], 'select-account-styled', 'account-button-styled', {
          [styles['compact']]: compact,
          compact,
          [styles['bordered']]: bordered,
          bordered,
          [styles['static']]: staticDisplay,
          static: staticDisplay
        })}
        data-test={dataTest || 'select-account'}
      >
        <Input
          type='select'
          name='select-account'
          options={options}
          value={selectedAccount ? String(selectedAccount.id) : ALL_ACCOUNTS_VALUE}
          onChange={handleChange}
          placeholder={tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.PLACEHOLDER', 'Select account')}
          error={error}
          hideErrorLine
          isSearchable={false}
          isLoading={loading}
          isDisabled={loading || staticDisplay}
          noOptionsMessage={noAccountsMessage}
          presentation={{
            filterOption: (option) => !(option as AccountOptionData).hidden,
            hideDropdownIndicator: staticDisplay,
            optionAttributes: (option) => ({
              'data-test': option.value === ALL_ACCOUNTS_VALUE ? 'all-accounts-menu-item' : 'account-menu-item'
            }),
            menuAttributes: { 'data-test': 'accounts-menu-content' },
            menuFooter
          }}
          dropdownPortal
        />
      </div>

      {isValidString(error) && (
        <div className={cn(surfaceStyles['selector-error'], styles['select-account-error'], 'select-account-error')}>
          {error}
        </div>
      )}
    </div>
  )
}

export default SelectAccount
